import {
  ChangeDetectionStrategy,
  Component,
  InjectionToken,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { Router } from '@angular/router';
import { UiAppSidebarItem } from '../../organisms/app-sidebar/app-sidebar.component';
import { UiBottomNavItem } from '../../organisms/bottom-navigation/bottom-navigation.types';
import { UiBreadcrumbItem } from '../../atoms/breadcrumb/breadcrumb.component';
import { UiTitleBarMode } from '../../organisms/title-bar/title-bar.types';
import { UiNotificationMenuItem } from '../../molecules/notification-menu/notification-menu.component';
import { UiAppLayoutComponent } from '../app-layout/app-layout.component';

/** Ruta del ítem «Mi cuenta». Si la app no la provee, el ítem solo emite el evento. */
export const UI_PORTAL_ACCOUNT_ROUTE = new InjectionToken<string>(
  'UI_PORTAL_ACCOUNT_ROUTE',
);

/** Badges y avisos del header. Si la app no los provee, cada página puede pasarlos por input. */
export interface UiPortalHeaderNotices {
  messageCount: number | null;
  notificationCount: number | null;
  notificationItems: readonly UiNotificationMenuItem[];
  /** `false` oculta el icono de mensajes. Sin valor, el portal lo muestra. */
  showMessages?: boolean | null;
  /** Destino de «Ver todas». Sin valor, el botón solo emite el evento. */
  viewAllHref?: string;
}

export const UI_PORTAL_HEADER_NOTICES = new InjectionToken<UiPortalHeaderNotices>(
  'UI_PORTAL_HEADER_NOTICES',
);

export type { UiAppSidebarItem, UiBottomNavItem };

/**
 * Template: shell compartido para los portales.
 *
 * Compone App Sidebar (`portal`) + App Header (`portal`) vía `ui-app-layout`.
 * «Mi cuenta» navega solo si la app provee `UI_PORTAL_ACCOUNT_ROUTE`.
 *
 * En móvil el sidebar pasa a panel superpuesto (lo abre el botón de menú) y,
 * si se reciben `bottomNavItems`, aparece la barra de navegación inferior.
 */
@Component({
  selector: 'ui-portal-layout',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiAppLayoutComponent],
  templateUrl: './portal-layout.component.html',
})
export class UiPortalLayoutComponent {
  private readonly router = inject(Router, { optional: true });
  private readonly accountRoute = inject(UI_PORTAL_ACCOUNT_ROUTE, {
    optional: true,
  });
  private readonly headerNotices = inject(UI_PORTAL_HEADER_NOTICES, {
    optional: true,
  });
  readonly logoSrc = input<string | null>('/images/logo_azul.png');
  readonly logoAlt = input('Diseño y Gestión');
  readonly navTitle = input<string | null>(null);
  /** Título del header solo en móvil. En escritorio se conserva `navTitle`. */
  readonly mobileNavTitle = input<string | null>(null);
  /** Controla la visibilidad de la barra de título de la vista. */
  readonly showTitleBar = input(true);
  readonly titleBarMode = input<UiTitleBarMode>('data-table');
  readonly title = input('');
  readonly breadcrumbItems = input<readonly UiBreadcrumbItem[]>([]);
  readonly breadcrumbHome = input<UiBreadcrumbItem | null>(null);
  readonly breadcrumbAriaLabel = input('Ruta de navegación');
  readonly mainLabel = input('Contenido del portal');

  readonly userAvatarLabel = input<string | null>(null);
  readonly notificationCount = input<number | null>(null);
  readonly messageCount = input<number | null>(null);
  readonly showMessages = input<boolean | null>(null);
  readonly notificationItems = input<readonly UiNotificationMenuItem[]>([]);
  readonly profileUserName = input('');
  readonly profileUserRole = input('');
  readonly profileUserEmail = input('');

  readonly sidebarItems = input<readonly UiAppSidebarItem[]>([]);
  readonly sidebarSelectedValue = input<string | null>(null);
  readonly sidebarCollapsed = input(false);
  readonly sidebarAriaLabel = input('Navegación del portal');

  /** Ítems de la barra inferior en móvil. Sin ítems no se muestra. */
  readonly bottomNavItems = input<readonly UiBottomNavItem[]>([]);
  readonly bottomNavAriaLabel = input('Navegación del portal');

  readonly sidebarItemSelect = output<string>();
  readonly sidebarToggle = output<void>();
  readonly logoutClick = output<void>();
  readonly profileMenuItemClick = output<{ id: string }>();
  readonly notificationItemSelect = output<UiNotificationMenuItem>();
  readonly notificationViewAll = output<void>();

  protected readonly resolvedMessageCount = computed(
    () => this.messageCount() ?? this.headerNotices?.messageCount ?? null,
  );
  protected readonly resolvedShowMessages = computed(() => {
    const fromPage = this.showMessages();
    if (fromPage !== null) return fromPage;
    return this.headerNotices?.showMessages ?? null;
  });
  protected readonly resolvedNotificationCount = computed(
    () => this.notificationCount() ?? this.headerNotices?.notificationCount ?? null,
  );
  protected readonly resolvedNotificationItems = computed(() => {
    const fromPage = this.notificationItems();
    if (fromPage.length) return fromPage;
    return this.headerNotices?.notificationItems ?? [];
  });

  protected onProfileMenuItem(event: { id: string }): void {
    this.profileMenuItemClick.emit(event);
    if (event.id !== 'account' || !this.accountRoute || !this.router) return;
    void this.router.navigateByUrl(this.accountRoute);
  }

  protected onNotificationItem(item: UiNotificationMenuItem): void {
    this.notificationItemSelect.emit(item);
    if (!item.href || !this.router) return;
    void this.router.navigateByUrl(item.href);
  }

  protected onNotificationViewAll(): void {
    this.notificationViewAll.emit();
    const href = this.headerNotices?.viewAllHref;
    if (!href || !this.router) return;
    void this.router.navigateByUrl(href);
  }
}
